import { SeoPageConfig, SeoPublicConfig, SeoSiteSettings } from "./types";

/**
 * The build-time fallback for the SEO configuration.
 *
 * The registry below mirrors `SeoDefaults.PUBLIC_PAGES` in the backend
 * (`com.reverse.job.portal.Seo.Constants.SeoDefaults`). The backend copy is the authoritative one -
 * it is what the admin "SEO Setup" screen edits and what the sitemap is built from - and it
 * replaces this list at runtime.
 *
 * The fallback exists so a public page still carries a correct title, description, canonical and
 * robots directive before the API answers, and if the API is unreachable. It is also the reason a
 * path that is not listed here is treated as private: an unknown route gets `noindex, nofollow`
 * rather than being published by accident.
 */

export const DEFAULT_BASE_URL = "https://nexgent.tech";
export const DEFAULT_SITE_NAME = "NexGent";
export const DEFAULT_TITLE = "NexGent | Private Leadership Hiring Platform";
export const DEFAULT_DESCRIPTION =
  "NexGent is an invitation-only hiring platform where senior leaders set their terms and let top teams apply to hire them. Stop applying, start being recruited.";

export const DEFAULT_SITE_SETTINGS: SeoSiteSettings = {
  baseUrl: DEFAULT_BASE_URL,
  siteName: DEFAULT_SITE_NAME,
  defaultTitle: DEFAULT_TITLE,
  defaultDescription: DEFAULT_DESCRIPTION,
  ogImage: "",
  twitterHandle: "",
  organizationLegalName: DEFAULT_SITE_NAME,
  organizationLogo: "",
  organizationDescription: DEFAULT_DESCRIPTION,
  locale: "en_US",
  googleSiteVerification: "",
  indexingEnabled: true,
  sitemapEnabled: true,
  extraRobotsRules: "",
};

export const DEFAULT_PAGES: SeoPageConfig[] = [
  {
    key: "home",
    path: "/",
    label: "Home",
    title: "NexGent | Private Leadership Hiring Platform",
    description:
      "NexGent is an invitation-only hiring platform where senior leaders set their terms and let top teams apply to hire them. Stop applying, start being recruited.",
    canonicalUrl: `${DEFAULT_BASE_URL}/`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 1,
    changeFreq: "weekly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "talents",
    path: "/talents",
    label: "Talent Density",
    title: "Talent Density | Reach the Leaders Other Recruiters Cannot | NexGent",
    description:
      "Explore the NexGent talent network: a dense, verified pool of senior leaders and recruiters who set their own terms, with the intelligence tools to search them.",
    canonicalUrl: `${DEFAULT_BASE_URL}/talents`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.9,
    changeFreq: "weekly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "subscription-plans",
    path: "/subscription-plans",
    label: "Subscription Plans",
    title: "Subscription Plans | Recruit Senior Leaders on NexGent",
    description:
      "Compare NexGent subscription plans for recruiters and hiring teams: credit-based access to verified senior talent, shared directories and engagement tracking.",
    canonicalUrl: `${DEFAULT_BASE_URL}/subscription-plans`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.8,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "pre-launch-pricing",
    path: "/pre-launch-pricing",
    label: "Pre-Launch Pricing",
    title: "Pre-Launch Pricing | Reserve Your Recruiter Tier | NexGent",
    description:
      "Reserve a recruiter pre-launch tier on NexGent and lock in early-bird pricing for access to the private leadership network.",
    canonicalUrl: `${DEFAULT_BASE_URL}/pre-launch-pricing`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.6,
    changeFreq: "weekly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "early-access",
    path: "/early-access",
    label: "Early Access Request",
    title: "Request Early Access | NexGent",
    description:
      "NexGent is invitation only and in limited early access. Request early access and tell us the role you are targeting so we can review your request.",
    canonicalUrl: `${DEFAULT_BASE_URL}/early-access`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.7,
    changeFreq: "weekly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "about",
    path: "/about",
    label: "About NexGent",
    title: "About NexGent | Purpose, Integrity & Platform Architecture",
    description:
      "Why NexGent exists: an invitation-only network for senior leaders and the companies that hire them, built on verification, transparency and a private hiring process.",
    canonicalUrl: `${DEFAULT_BASE_URL}/about`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.7,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "how-it-works",
    path: "/how-it-works",
    label: "How NexGent Works",
    title: "How NexGent Works | Private Leadership Hiring Process",
    description:
      "See how NexGent works end to end: request access, get verified, build your profile, set your terms and let the right teams apply to hire you.",
    canonicalUrl: `${DEFAULT_BASE_URL}/how-it-works`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.8,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "faq",
    path: "/faq-policy",
    label: "FAQ",
    title: "Frequently Asked Questions | NexGent",
    description:
      "Answers to the questions candidates, recruiters and admins ask most about NexGent: access, verification, credits, subscriptions and privacy.",
    canonicalUrl: `${DEFAULT_BASE_URL}/faq-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.7,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "trust-transparency",
    path: "/trust-transparency",
    label: "Trust & Transparency",
    title: "Trust & Transparency | How NexGent Verifies People",
    description:
      "How NexGent verifies candidates and recruiters, how it handles reports, and what it publishes about the trust and safety of the network.",
    canonicalUrl: `${DEFAULT_BASE_URL}/trust-transparency`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.6,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "contact",
    path: "/contact-us",
    label: "Contact",
    title: "Contact NexGent | Talk to the Team",
    description:
      "Get in touch with the NexGent team about access, verification, recruiting or support. Response times for each channel are listed on the page.",
    canonicalUrl: `${DEFAULT_BASE_URL}/contact-us`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.5,
    changeFreq: "yearly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "security-policy",
    path: "/security-policy",
    label: "Security Policy",
    title: "Security at NexGent | Security Policy",
    description:
      "How NexGent protects accounts, credentials and platform data: account security, credential handling, monitoring and incident response.",
    canonicalUrl: `${DEFAULT_BASE_URL}/security-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.4,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "privacy-policy",
    path: "/privacy-policy",
    label: "Privacy Policy",
    title: "Privacy Policy & Data Architecture | NexGent",
    description:
      "What personal data NexGent collects, why it is collected, how long it is kept and the rights every candidate and recruiter holds over it.",
    canonicalUrl: `${DEFAULT_BASE_URL}/privacy-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.4,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "terms-and-condition",
    path: "/terms-and-condition",
    label: "Terms & Conditions",
    title: "Terms of Service & Platform Governance | NexGent",
    description:
      "The terms that govern the use of NexGent: eligibility, account responsibilities, acceptable use and how the platform is governed.",
    canonicalUrl: `${DEFAULT_BASE_URL}/terms-and-condition`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.4,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "refund-policy",
    path: "/refund-and-cancellation-policy",
    label: "Refund & Cancellation",
    title: "Refund & Cancellation Policy | NexGent",
    description:
      "How NexGent subscriptions renew, when a plan can be cancelled and how refunds are reviewed and paid out.",
    canonicalUrl: `${DEFAULT_BASE_URL}/refund-and-cancellation-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.3,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "recruiter-verification-policy",
    path: "/recruiter-verification-policy",
    label: "Recruiter Verification",
    title: "Recruiter Verification Policy | NexGent",
    description:
      "Why every recruiter on NexGent is verified, what the review covers and what happens when a recruiter does not meet the standard.",
    canonicalUrl: `${DEFAULT_BASE_URL}/recruiter-verification-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.3,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "candidate-verification-policy",
    path: "/candidate-verification-policy",
    label: "Candidate Verification",
    title: "Candidate Verification Policy | NexGent",
    description:
      "How NexGent verifies the leaders on the network, what verification proves and how candidates keep their profile accurate.",
    canonicalUrl: `${DEFAULT_BASE_URL}/candidate-verification-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.3,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "credit-usage-policy",
    path: "/credit-usage-policy",
    label: "Credit Usage",
    title: "Credit Usage Policy | How NexGent Credits Work",
    description:
      "What a NexGent credit is, when credits are consumed, how they are refunded and how to keep an eye on the balance.",
    canonicalUrl: `${DEFAULT_BASE_URL}/credit-usage-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.3,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
  {
    key: "cookie-policy",
    path: "/cookie-policy",
    label: "Cookie Policy",
    title: "Cookie Policy | NexGent",
    description:
      "The cookies NexGent sets, what each one is for, how long it lives and how to control them in your browser.",
    canonicalUrl: `${DEFAULT_BASE_URL}/cookie-policy`,
    ogImage: "",
    ogType: "",
    indexable: true,
    sitemapInclude: true,
    priority: 0.3,
    changeFreq: "monthly",
    structuredDataType: "NONE",
    structuredDataJson: "",
    lastModified: "",
    customized: false,
  },
];

/**
 * The path prefixes a crawler must stay out of. This mirrors
 * `SeoDefaults.PRIVATE_PATH_PREFIXES` in the backend and is used to build the static
 * `robots.txt` shipped with the build.
 */
export const PRIVATE_PATH_PREFIXES: string[] = [
  "/admin",
  "/candidate",
  "/recruiter",
  "/data-service",
  "/payment",
  "/invitations",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/registration/",
  "/invite/",
  "/join/",
  "/share/",
  "/early-access-registration/",
  "/l/",
  "/test",
  "/unauthorized",
  "/prelaunch",
];

/**
 * Titles for the routes that are deliberately kept out of the index. They are only ever shown to a
 * person, but a generic title on the sign-in screen reads as a bug. The lookup is by prefix, so
 * `/login` and `/candidate/edit-profile` both resolve.
 */
export const PRIVATE_PAGE_TITLES: Record<string, string> = {
  "/login": "Sign In | NexGent",
  "/forgot-password": "Reset Your Password | NexGent",
  "/reset-password": "Choose a New Password | NexGent",
  "/registration": "Create Your NexGent Account | NexGent",
  "/invite": "Your NexGent Invitation | NexGent",
  "/join": "Accept Your NexGent Invitation | NexGent",
  "/early-access-registration": "Complete Early Access Registration | NexGent",
  "/share": "Shared Directory | NexGent",
  "/l": "NexGent",
  "/test": "NexGent",
  "/unauthorized": "Access Denied | NexGent",
  "/payment": "Payment | NexGent",
  "/invitations": "Invitations | NexGent",
  "/candidate": "Candidate Dashboard | NexGent",
  "/recruiter": "Recruiter Dashboard | NexGent",
  "/admin": "Admin | NexGent",
  "/data-service": "Data Service | NexGent",
  "/prelaunch": "NexGent Launch",
};

export const DEFAULT_SEO_CONFIG: SeoPublicConfig = {
  site: DEFAULT_SITE_SETTINGS,
  pages: DEFAULT_PAGES,
};

/** Normalises a browser path so `/about` and `/about/` resolve to the same page. */
export function normalizePath(pathname: string): string {
  if (!pathname) return "/";
  const trimmed = pathname.split("?")[0].split("#")[0];
  if (!trimmed) return "/";
  if (trimmed.length > 1 && trimmed.endsWith("/")) {
    return trimmed.replace(/\/+$/, "") || "/";
  }
  return trimmed;
}
